import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import { deleteImage, getDiskImages, paginateImages } from '@go-client/lib/api/image';
import {
  BubbleAccordion,
  BubbleButton,
  BubbleDebounce,
  BubbleDrawer,
  BubbleFormGroup,
  BubbleImageThumbnail,
  BubbleInput,
} from '@go-client/components/BubbleUI';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';

import './ImageLibrary.scss';
import Libraries from './Libraries';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { Image, FSImage } from '@go-shared/types/image';
import { canDeleteImages, canManageLibraries } from '@go-shared/lib/permissions';
import ImageFormik, {
  ImageFormikMode,
  ImageFormikRef,
} from '@go-client/components/Formik/ImageFormik';
import BubbleDrawerBasicContainer from '@go-client/components/BubbleUI/BubbleDrawerHeader';
import ImageUsageList from '@go-client/components/ImageUsageList';
import { DEFAULT_PER_PAGE, PaginationMetadata } from '@go-shared/types/pagination';
import { SortOrder } from '@go-shared/types/userActionRecord';
import { BubbleTablePaginationFooter } from '@go-client/components/BubbleUI/BubbleTablePaginationFooter';
import { getExtensionFromUrl, getMimeFromExtension } from '@go-client/context/ImageCropperContext';
import { Alert } from '@spark-labs/avocado-ui';
import { readQs } from '@go-shared/lib/querystring';

interface ImageLibraryFilters {
  viewDisk: boolean;
}

type PaginationParams<T> = {
  page: number;
  perPage: number;
  sortBy: keyof T;
  sortDirection: SortOrder;
  search: string;
};

export default function ImageLibrary(props: RouteComponentProps) {
  const { ...rest } = props;

  const formikRef = useRef<ImageFormikRef>(null);

  const { activeProperty: property } = useActiveProperty();
  const { user } = useUserInfo();

  const [loading, setLoading] = useState(false);
  const [cloudItems, setCloudItems] = useState<Image[] | undefined>(undefined);
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>(
    undefined
  );
  const [diskItems, setDiskItems] = useState<FSImage[] | undefined>(undefined);
  const [mode, setMode] = useState<ImageFormikMode | undefined>(undefined);
  const [filters, setFilters] = useState<ImageLibraryFilters>({
    viewDisk: false,
  });

  const queries = readQs();

  const [paginationParams, setPaginationParams] = useState<PaginationParams<Image>>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    sortBy: 'updatedAt',
    sortDirection: SortOrder.DESC,
    search: typeof queries.search === 'string' ? queries.search : '',
  });

  const contentRef = useRef<HTMLDivElement | null>(null);

  // this will scroll the page to top when search is modified
  useEffect(() => {
    // We want to scroll to top only when we are in page 1 or paginationParams are reset
    if (paginationParams.page !== 1) {
      return;
    }

    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [paginationParams, contentRef]);

  useEffect(() => {
    refresh();
  }, [paginationParams, property]);

  function refresh() {
    fetchDataBundle().catch(console.error);
  }

  async function fetchDataBundle() {
    try {
      setLoading(true);
      await Promise.all([fetchCloudImages(), fetchDiskImages()]);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCloudImages() {
    const results = await paginateImages({
      page: paginationParams.page,
      perPage: paginationParams.perPage,
      sortBy: paginationParams.sortBy,
      sortDirection: paginationParams.sortDirection,
      search: paginationParams.search,
    });
    setCloudItems(
      paginationParams.page > 1 ? [...(cloudItems ?? []), ...results.data] : results.data
    );
    setPaginationMetadata(results.meta);
  }

  async function fetchDiskImages() {
    const results = await getDiskImages();
    setDiskItems(results);
  }

  async function handleDeleteImageClick(image: Image) {
    await deleteImage(image.key);
    setMode(undefined);
    refresh();
  }

  async function handleAddImageClick() {
    setMode({ type: 'create', page: 'edit' });
  }

  async function onSubmit() {
    setMode(undefined);
    refresh();
  }

  const locked = !canDeleteImages(user);

  function headerFn() {
    return (
      <>
        <BubbleButton className="h-full" appearance="squared" variant="titanium" block>
          Images
        </BubbleButton>
      </>
    );
  }

  function bodyFn() {
    return (
      <>
        {filters.viewDisk ? (
          <>
            <div className="grid grid-cols-5 gap-2">
              {diskItems?.map(item => (
                <BubbleImageThumbnail
                  key={item.key}
                  purpose="library"
                  readOnly={locked}
                  thumbnail={{
                    key: item.key,
                    name: item.name,
                    extension: getExtensionFromUrl(item.key) || '?',
                    mimeType: getMimeFromExtension(getExtensionFromUrl(item.key)) || '?',
                    size: 0, // size is not available for disk images
                  }}
                />
              ))}
            </div>
            {(!diskItems || diskItems.length === 0) && (
              <div className="text-center text-slate py-5-i">No items to show</div>
            )}
          </>
        ) : (
          <>
            <BubbleDebounce
              onTrigger={[
                (search: string) => setPaginationParams({ ...paginationParams, page: 1, search }),
                500,
              ]}
            >
              {({ debounce }) => (
                <BubbleInput
                  type="text"
                  block
                  value={paginationParams.search}
                  inputClassName="outline-none-i"
                  onValue={debounce}
                  placeholder="Search..."
                  className="pb-3 sticky z-10 bg-blue-lightest rounded-0-i"
                  style={{ top: 182 }}
                />
              )}
            </BubbleDebounce>

            <div className="flex flex-wrap gap-4">
              {cloudItems?.map(item => (
                <BubbleImageThumbnail
                  readOnly={locked}
                  key={item.key}
                  purpose="library"
                  thumbnail={item}
                  className="w-48"
                  onClick={() => setMode({ type: 'edit', edit: item, page: 'edit' })}
                  onDeleteClick={() => handleDeleteImageClick(item)}
                />
              ))}
            </div>
            {(!cloudItems || cloudItems.length === 0) && (
              <div className="text-center text-slate py-5-i">No items to show</div>
            )}
            {paginationMetadata && (
              <BubbleTablePaginationFooter
                paginationMetadata={paginationMetadata}
                perPage={paginationParams.perPage}
                onLoadMoreClick={() =>
                  setPaginationParams({ ...paginationParams, page: paginationParams.page + 1 })
                }
                onPerPageChange={value =>
                  setPaginationParams({ ...paginationParams, page: 1, perPage: value })
                }
              />
            )}
          </>
        )}
      </>
    );
  }

  async function closeImageForm() {
    if (formikRef.current && formikRef.current.dirty()) {
      const ans = await Alert.question(
        'You have unsaved changes. Are you sure you wish to close this form?'
      );
      if (!ans) return;
    }
    setMode(undefined);
  }

  function outerFn() {
    return (
      <>
        {mode && (
          <BubbleDrawer onClose={closeImageForm} containerClassName="bg-eggshell">
            <BubbleDrawerBasicContainer
              title={`${mode.type === 'edit' ? 'Edit' : 'Create'} Content`}
              subtitle="Image Details"
              onClose={closeImageForm}
            >
              <div className="flex items-center mb-8 -mx-8">
                <BubbleButton
                  block
                  appearance="squared"
                  onClick={() => setMode({ ...mode, page: 'edit' })}
                  variant={mode.page === 'edit' ? 'titanium' : 'smoke'}
                >
                  Details
                </BubbleButton>
                <BubbleButton
                  block
                  appearance="squared"
                  onClick={async () => {
                    if (formikRef.current && formikRef.current.dirty()) {
                      await Alert.warning(
                        'Please save your changes first before switching to the insights tab.'
                      );
                    } else {
                      setMode({ ...mode, page: 'insights' });
                    }
                  }}
                  variant={mode.page === 'insights' ? 'titanium' : 'smoke'}
                >
                  Usage
                </BubbleButton>
              </div>
              {mode.page === 'edit' ? (
                <ImageFormik
                  ref={formikRef}
                  mode={mode}
                  onSubmit={onSubmit}
                  actionsAppearance="drawer-float"
                  subnavClassName="-mx-8"
                  onDeleteImageClick={
                    mode.edit ? () => handleDeleteImageClick(mode.edit!) : undefined
                  }
                />
              ) : (
                <ImageUsageList image={mode.edit} />
              )}
            </BubbleDrawerBasicContainer>
          </BubbleDrawer>
        )}
      </>
    );
  }

  function sideFn() {
    return (
      <>
        <div className="flex flex-col relative items-stretch p-4 bg-titanium-darker">
          <div className="grid gap-2">
            {canManageLibraries(user) && (
              <BubbleButton
                variant="lime"
                onClick={handleAddImageClick}
                className="text-left"
                icon="sparkles"
              >
                Create Image
              </BubbleButton>
            )}
          </div>
        </div>

        <BubbleAccordion
          variant="titanium"
          className="flex-grow flex flex-col border-t border-forest-darkest"
          labels={['Filters']}
          open={[0]}
          locked={[0]}
        >
          <div className="grid gap-4 p-4">
            <BubbleFormGroup label="Options" labelClassName="text-white-i">
              <div className="grid gap-1">
                <div className="text-white flex relative">
                  <BubbleInput
                    type="checkbox"
                    variant="titanium"
                    checked={filters.viewDisk}
                    inputClassName="stretched-link"
                    className="mr-1"
                    onValue={(checked: boolean) => {
                      setFilters({ ...filters, viewDisk: checked });
                    }}
                  />
                  <span>View Disk Images</span>
                </div>
              </div>
            </BubbleFormGroup>
          </div>
        </BubbleAccordion>
      </>
    );
  }

  return (
    <Libraries
      user={user}
      active="assets"
      property={property}
      loading={loading}
      contentRef={contentRef}
      headerFn={headerFn}
      bodyFn={bodyFn}
      outerFn={outerFn}
      sideFn={sideFn}
      {...rest}
    />
  );
}
